import clsx from "clsx";
import { ReactNode } from "react";

import { Pill } from "@/components/Interface/Pill/Pill";
import { getCurrency } from "@/utils/translationHelpers";

export type PriceRowVariant = "default" | "strong" | "base" | "strong-xs";

const priceRowTextClasses: Record<PriceRowVariant, string> = {
  default: "text-body-xs leading-md",
  strong: "text-body-base font-semibold leading-none",
  // Same base size as "strong" but at regular weight - a non-bold value next
  // to a bold label (e.g. the SIM detail rows).
  base: "text-body-base leading-none",
  "strong-xs": "text-body-xs font-semibold leading-none",
};

/** One `dire-table` row: a label on the left and its amount on the right. */
export const PriceRow = ({
  label,
  value,
  variant = "default",
  valueVariant,
  withBackdrop,
  withDivider,
  withTopDivider,
  equalColumns,
}: {
  label: ReactNode;
  value: ReactNode;
  /** "strong" is the semibold styling of the product and total rows. */
  variant?: PriceRowVariant;
  /** Override the variant for the value (right) column only. */
  valueVariant?: PriceRowVariant;
  withBackdrop?: boolean;
  withDivider?: boolean;
  /**
   * A dark divider line above this row, e.g. above "Total" - independent of
   * `withDivider` on whichever row happens to precede it, so the line above
   * the total shows up regardless of which optional discount/credit rows
   * are present.
   */
  withTopDivider?: boolean;
  equalColumns?: boolean;
}) => (
  <div
    className={clsx(
      "flex w-full items-stretch",
      withBackdrop && "rounded-md bg-surface-quaternary",
      withTopDivider && "border-t border-border-primary",
    )}
  >
    <div
      className={clsx(
        // The divider sits inside the cell, on the fixed-height element: on
        // the row it would add an extra pixel. min-h, not h, so a wrapped
        // label or value can still grow the row instead of overflowing it.
        "flex min-h-14 min-w-0 flex-1 items-center px-4",
        withDivider && "border-b border-border-tertiary",
      )}
    >
      <div
        className={clsx(
          // A label without a hyphen or space (e.g. a long German compound word) has
          // nowhere to wrap by default and overflows into the value column
          // instead - break-words forces a wrap once it runs out of room.
          "min-w-0 flex-1 break-words text-foreground-primary",
          priceRowTextClasses[variant],
        )}
      >
        {label}
      </div>
    </div>
    <div
      className={clsx(
        "flex min-h-14 items-center justify-end px-4",
        // A minimum, not a fixed width: a value too wide to fit (e.g. a
        // non-wrapping phone number) must push into the label's flexible
        // column instead of overflowing past this one. Capped at half so it
        // can still shrink - and wrap, via break-words below - rather than
        // force the row wider than its container on a narrow viewport.
        equalColumns ? "flex-1" : "min-w-[120px] max-w-[50%]",
        withDivider && "border-b border-border-tertiary",
      )}
    >
      <div
        className={clsx(
          "flex flex-col items-end gap-1 break-words text-right text-foreground-primary",
          priceRowTextClasses[valueVariant ?? variant],
        )}
      >
        {value}
      </div>
    </div>
  </div>
);

/** Chip displaying a deduction (discount or credit) formatted as "- CHF X.YY". */
export const DeductionChip = ({ amount }: { amount: number }) => (
  <Pill
    className="bg-surface-accent-mint text-foreground-primary"
    size="sm"
    text={`- ${getCurrency(amount, "CHF")}`}
  />
);
